import { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Box, Button, Grid2, Typography } from "@mui/material";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router";
import { useSnackbar } from "notistack";
import { RHFTextField } from "@rentablo/components";
import { LoadingButton } from "@rentablo/components";
import {
  useRecommendCashback,
  useRecommendationCode,
} from "@rentablo/api/apiv1";
import { useMyself } from "@api/v1/hooks/useMyself";

const Form = styled.form`
  display: flex;
  padding: ${({ theme }) => theme.spacing(4, 0)};
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

const FormActions = styled.div`
  display: flex;
  width: 100%;

  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: ${({ theme }) => theme.spacing(2, 0)};
    flex-direction: column;
    align-items: unset;
  }
`;

interface FormFields {
  senderName: string;
  receiverName: string;
  receiverEmail: string;
  emailBody: string;
}

export const RecommendUserForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: myself } = useMyself();
  const { enqueueSnackbar } = useSnackbar();
  const recaptchaKey = "6LfD74YUAAAAAKe3ypGFG1sjbskntjlPPj8FOhis";
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { mutate: recommend, isPending } = useRecommendCashback();
  const { data: discountToken } = useRecommendationCode();

  const schema = Yup.object().shape({
    senderName: Yup.string().required(t("form.errors.required")),
    receiverName: Yup.string().required(t("form.errors.required")),
    receiverEmail: Yup.string()
      .required(t("form.errors.required"))
      .email(t("form.errors.wrongEmailFormat")),
    emailBody: Yup.string(),
  });

  const { handleSubmit, control, reset } = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      senderName: myself?.name,
      receiverName: "",
      receiverEmail: "",
      emailBody: "",
    },
  });

  useEffect(() => {
    reset({ senderName: myself?.name });
  }, [myself?.name, reset]);

  const onSubmit = async (form: FormFields) => {
    const token = await recaptchaRef.current?.executeAsync();
    try {
      await recommend(
        {
          emailParams: {
            ...form,
            challengeToken: token!,
          },
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("domains.cashback.invite.success"), {
              variant: "success",
            });
          },
          onError: () => {
            enqueueSnackbar(t("form.errors.somethingWentWrong"), {
              variant: "error",
            });
          },
        }
      );
      recaptchaRef.current?.reset();
      reset();
    } catch (err) {
      // no-err
    }
  };

  return (
    <Box maxWidth={700}>
      <Typography variant="h6">
        {t("domains.cashback.invite.subtitle")}
      </Typography>
      <Typography variant="body1" mb={2}>
        {t("domains.cashback.invite.description")}
      </Typography>
      <Typography variant="h6">
        {`${t("domains.cashback.invite.promotion")} - ${discountToken}`}
      </Typography>
      <Typography variant="body1" mb={3}>
        {t("domains.cashback.invite.subtitle2")}
      </Typography>
      <Form onSubmit={(e) => handleSubmit(onSubmit)(e)}>
        <Grid2 container spacing={4}>
          <Grid2
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <RHFTextField
              label={t("domains.cashback.invite.myName")}
              name="senderName"
              control={control}
              fullWidth
            />
          </Grid2>
          <Grid2
            size={{
              md: 6,
              xs: 12,
            }}
          >
            <RHFTextField
              label={t("domains.cashback.invite.friendName")}
              name="receiverName"
              control={control}
              fullWidth
            />
          </Grid2>
        </Grid2>
        <Grid2 container spacing={6}>
          <Grid2
            size={{
              md: 12,
              xs: 12,
            }}
          >
            <RHFTextField
              label={t("domains.cashback.invite.email")}
              name="receiverEmail"
              control={control}
              fullWidth
            />
          </Grid2>
          <Grid2
            size={{
              md: 12,
              xs: 12,
            }}
          >
            <RHFTextField
              label={t("domains.cashback.invite.message")}
              name="emailBody"
              multiline
              minRows={4}
              control={control}
              fullWidth
            />
          </Grid2>
        </Grid2>
        <FormActions>
          <Button variant="tertiary" onClick={() => navigate("/cashback")}>
            {t("domains.cashback.form.cancel")}
          </Button>
          <LoadingButton isFetching={isPending} type="submit" variant="primary">
            {t("domains.cashback.form.recommend")}
          </LoadingButton>
        </FormActions>
        <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey} size="invisible" />
      </Form>
    </Box>
  );
};
